import { Injectable } from '@angular/core';
import { FolderPropertyCodes } from 'src/app/core/constantes/folder-property-codes';
import { HttpError } from 'src/app/core/http-error';
import { MenuType } from 'src/app/models/events/folder-menu-refresh-event';
import { Folder } from 'src/app/models/server/folder';
import { IFolderWorkflowStepsSnapshot } from 'src/app/models/server/interface/folder-workflow-steps-snapshot.interface';
import { StandByDatas } from 'src/app/models/server/stand-by-datas';
import { EventsManagerService } from 'src/app/services/events/events-manager.service';
import { SpinnerService } from '../core/spinner.service';
import { FolderDocumentsService } from '../http/folder-documents.service';
import { FoldersService } from '../http/folders.service';
import { AttributeDefinitionsStateService } from '../state/attribute-definitions-state.service';
import { BoxesStateService } from '../state/boxes-state.service';
import { ConnectedUsersStateService } from '../state/connected-users-state.service';
import { ViewsStateService } from '../state/views-state.service';
import { NotificationService } from './notification.service';

@Injectable({
    providedIn: 'root',
})
export class FolderActionsService {
    /**
     * Constructeur.
     * @param boxesStateService : Service de persistence de l'état des bannettes.
     * @param connectedUserStateService : Service de persistence de l'état de l'utilisateur connecté.
     * @param foldersService : Service de gestion des dossiers.
     * @param notificationService : Service de notification.
     * @param enumHelper : Classe utilitaire pour manipuler les Enum.
     * @param displayLabelHelper : Classe utilitaire pour l'affichage des libellés.
     */
    constructor(
        private boxesStateService: BoxesStateService,
        private connectedUserStateService: ConnectedUsersStateService,
        private foldersService: FoldersService,
        protected notificationService: NotificationService,
        private spinner: SpinnerService,
        private eventsManagerService: EventsManagerService,
        private folderDocumentsService: FolderDocumentsService,
        private attributeDefinitionsStateService: AttributeDefinitionsStateService,
        private viewsStateService: ViewsStateService
    ) {}

    /**
     * Supprime un dossier
     * @param folder : Instance du dossier à supprimer.
     * @param displayNotification : Indique si une notification doit être affichée à l'utilisateur.
     */
    public deleteFolder(folder: Folder, displayNotification: boolean = true): void {
        folder.isDelete = true;

        this.foldersService.delete(folder.viewId, folder.id).subscribe((x) => {
            // Suppression du dossiers de la liste
            this.eventsManagerService.emitListFolderDeletedEvent(folder.id);
            this.eventsManagerService.emitFollowedFoldersDeletedEvent(folder.id);

            // Fermeture des dossiers ouverts.
            this.eventsManagerService.emitFolderTabCloseRequestEvent(folder.id, false);

            // Mise à jour des compteurs
            if (folder.boxId) {
                this.eventsManagerService.emitBoxCounterUpdatedEvent(folder.boxId, -1);
            } else {
                this.eventsManagerService.emitViewCounterUpdatedEvent(folder.viewId, -1);
            }

            if (displayNotification) {
                this.notificationService.displaySuccess(`Le dossier '${folder.name}' a été supprimé`, '');
            }
        });
    }

    /**
     * Crée un dossier.
     * @param folder : Instance du dossier à créer.
     * @param displayNotification : Indique si une notification doit être affichée à l'utilisateur.
     */
    public addFolder(folder: Folder, displayNotification: boolean = true): void {
        this.foldersService.add(this.viewsStateService.getSelectedView().id, folder).subscribe(
            (x) => {
                this.eventsManagerService.emitListRefreshEvent();

                if (x.boxId) {
                    this.eventsManagerService.emitBoxCounterUpdatedEvent(x.boxId, 1);
                } else {
                    this.eventsManagerService.emitViewCounterUpdatedEvent(x.viewId, 1);
                }

                if (displayNotification) {
                    this.notificationService.displaySuccess(`Le dossier '${x.name}' a été créé`, '');
                }
            },
            (error: HttpError) => {
                this.notificationService.displayError(error.reasonPhrase, `Le dossier n'a pas pu être créé : ${error.message}`);
            }
        );
    }

    /**
     * Fusionne un dossier dans un autre.
     * @param sourceFolder : Instance du dossier source.
     * @param destinationFolder : Instance du dossier destination.
     */
    public mergeFolder(sourceFolder: Folder, destinationFolder: Folder) {
        this.foldersService.merge(sourceFolder.viewId, sourceFolder.id, destinationFolder.id).subscribe((folder) => {
            this.eventsManagerService.emitListFolderDeletedEvent(sourceFolder.id);
            this.eventsManagerService.emitFollowedFoldersDeletedEvent(sourceFolder.id);
            this.eventsManagerService.emitFolderTabCloseRequestEvent(sourceFolder.id, false);

            this.folderDocumentsService.list(destinationFolder.viewId, destinationFolder.id).subscribe((documents) => {
                this.eventsManagerService.emitDocumentsUpdatedEvent(destinationFolder.id, documents);
            });

            this.eventsManagerService.emitHistorySectionUpdatedEvent(destinationFolder.viewId, destinationFolder.id);

            if (sourceFolder.boxId) {
                this.eventsManagerService.emitBoxCounterUpdatedEvent(sourceFolder.boxId, -1);
                this.eventsManagerService.emitBoxCounterUpdatedEvent(destinationFolder.boxId, 1);
            } else {
                this.eventsManagerService.emitViewCounterUpdatedEvent(sourceFolder.viewId, -1);
                this.eventsManagerService.emitViewCounterUpdatedEvent(destinationFolder.viewId, 1);
            }

            this.notificationService.displaySuccess(`Le dossier '${sourceFolder.name}' a été fusionné au dossier '${destinationFolder.name}'`, '');
        });
    }

    /**
     * crée des copies d'un dossier.
     * @param sourceFolder : Instance du dossier source.
     * @param duplicateCount : Nombre de copies du dossier à réaliser.
     */
    public duplicateFolder(sourceFolder: Folder, duplicateCount: number) {
        this.spinner.show('folders-container-spinner');
        this.foldersService.duplicate(sourceFolder.viewId, sourceFolder.id, duplicateCount).subscribe((folder) => {
            this.eventsManagerService.emitListRefreshEvent();
            this.eventsManagerService.emitFollowedFoldersRefreshEvent();

            this.eventsManagerService.emitHistorySectionUpdatedEvent(sourceFolder.viewId, sourceFolder.id);

            if (sourceFolder.boxId) {
                this.eventsManagerService.emitBoxCounterUpdatedEvent(sourceFolder.boxId, duplicateCount);
            } else {
                this.eventsManagerService.emitViewCounterUpdatedEvent(sourceFolder.viewId, duplicateCount);
            }

            this.notificationService.displaySuccess(`'${duplicateCount}' copie(s) du dossier '${sourceFolder.name}' ont été réalisées'`, '');
            this.spinner.hide('folders-container-spinner');
        });
    }

    /**
     * Met à jour l'état d'une étape de workflow.
     * @param folderId : Id du dossier.
     * @param workflowStepName : Nom de l'étape du workflow.
     * @param state : Etat du workflow.
     */
    public updateFolderWorkflowStepState(viewId: number, folderId: string, workflowStepsSnapshot: IFolderWorkflowStepsSnapshot, newStateId: number): void {
        this.spinner.show('workflow-spinner');
        this.foldersService.updateWorkflowStepsSnapshot(viewId, folderId, workflowStepsSnapshot.workflowStepId, newStateId).subscribe(
            (x) => {
                workflowStepsSnapshot = x;
                this.spinner.hide('workflow-spinner');
            },
            (error: any) => {
                this.spinner.hide('workflow-spinner');
            }
        );
    }

    /**
     * Active une étape de workflow d'un dossier.
     * @param folderId : Id du dossier
     * @param workflowStepsSnapshot : Instance de l'étape du workflow.
     */
    public activateFolderWorkflowStep(viewId: number, folderId: string, workflowStepsId: number) {
        this.foldersService.activateFolderWorkflowStep(viewId, folderId, workflowStepsId).subscribe((x) => {});
    }

    /**
     * Finalise une étape de workflow d'un dossier.
     * @param folderId : Id du dossier
     * @param workflowStepsSnapshot : Instance de l'étape du workflow.
     */
    public completeFolderWorkflowStep(viewId: number, folderId: string, workflowStepsSnapshot: IFolderWorkflowStepsSnapshot) {
        this.foldersService.completeFolderWorkflowStep(viewId, folderId, workflowStepsSnapshot.workflowStepId).subscribe((x) => {
            workflowStepsSnapshot = x;
        });
    }

    /**
     * Déplace un dossier d'une bannette vers une autre bannette.
     * @param folderViewId Id de la vue à laquelle appartient le dossier.
     * @param folder: Instance du dossier
     * @param targetBoxId: Identifiant de la bannette de destination.
     */
    public moveToBox(folderViewId: number, folder: Folder, targetBoxId: string): void {
        this.foldersService.moveToBox(folderViewId, folder.id, targetBoxId).subscribe((x) => {
            this.eventsManagerService.emitListRefreshEvent();

            this.eventsManagerService.emitHistorySectionUpdatedEvent(folder.viewId, folder.id);
            this.eventsManagerService.emitAttributesSectionUpdatedEvent(folderViewId, folder.id);

            this.eventsManagerService.emitBoxCounterUpdatedEvent(folder.boxId, -1);
            this.eventsManagerService.emitBoxCounterUpdatedEvent(targetBoxId, 1);

            const boxInfos = this.boxesStateService.getBoxesList().find((b) => b.id == targetBoxId);
            this.notificationService.displaySuccess(`Le dossier '${folder.name}' (${folder.id}) a été déplacé vers la bannette '${boxInfos.name}'`, '');
        });
    }

    /**
     * Met à jour un attribut d'un dossier
     * @param folderViewId Id de la vue à laquelle appartient le dossier.
     * @param folderId: Identifiant du dossier
     * @param folder: Information du dossier
     * @param attributeDefinitionId: Id de la definition de l'attribut à mettre à jour.
     */
    public updateFolderAttribute(folderViewId: number, folder: Folder, attributeDefinitionId: number, oldValue: any, newValue: any): void {
        this.foldersService.updateAttribute(folderViewId, folder.id, folder, attributeDefinitionId).subscribe((x) => {
            // TODO : Ne faire qu'un update de dossiers si aucun filtre ou tri n'existe sur l'attribut modifié
            this.eventsManagerService.emitListRefreshEvent();
            this.eventsManagerService.emitFollowedFoldersRefreshEvent();

            this.eventsManagerService.emitAttributesSectionUpdatedEvent(folderViewId, x);
            this.eventsManagerService.emitHistorySectionUpdatedEvent(folder.viewId, folder.id);
            this.eventsManagerService.emitFavoriteSectionUpdatedEvent(folderViewId, x);

            // Si l'attribut correspond à BoxId --> decrementer la bannette dans laquelle se trouvait le dossier, incrémenter la nouvelle bannette
            const boxIdAttributeDefinition = this.attributeDefinitionsStateService
                .getAttributeDefinitions(this.viewsStateService.getSelectedView().id)
                .find((x) => x.propertyCode === FolderPropertyCodes.BOX_ID);
            if (boxIdAttributeDefinition && attributeDefinitionId === boxIdAttributeDefinition.id) {
                this.eventsManagerService.emitBoxCounterUpdatedEvent(oldValue, -1);
                this.eventsManagerService.emitBoxCounterUpdatedEvent(newValue, +1);
            }

            // Si l'attribut correspond à Name --> Renommage de l'onglet du dossier s'il est ouvert
            const nameAttributeDefinition = this.attributeDefinitionsStateService
                .getAttributeDefinitions(this.viewsStateService.getSelectedView().id)
                .find((x) => x.propertyCode === FolderPropertyCodes.NAME);
            if (attributeDefinitionId === nameAttributeDefinition.id) {
                this.eventsManagerService.emitFolderRenamedEvent(folder.id, newValue);
            }

            this.notificationService.displaySuccess(`Le dossier '${folder.name}' a été modifié`, '');
        });
    }

    /**
     *Bascule l'état de lecture d'un dossier à lu ou non lu.
     * @param folder: Instance du dossier.
     * @param readState: Etat de lecture (lu ou non lu)
     */
    public toggleReadStatus(folder: Folder, readState: boolean): void {
        this.foldersService.toggleReadStatus(folder.viewId, folder.id, readState).subscribe((x) => {
            this.eventsManagerService.emitListFolderUpdatedEvent(folder.viewId, x);
            this.eventsManagerService.emitFollowedFoldersUpdatedEvent(folder.viewId, x);
            this.eventsManagerService.emitHistorySectionUpdatedEvent(folder.viewId, folder.id);
        });
    }

    /**
     * Place le dossier en veille (stand-by).
     * @param folderId: Id du dossier.
     * @param standByDatas: Données du mode veille.
     */
    public setInStandByState(folder: Folder, standByDatas: StandByDatas): void {
        this.spinner.show('folders-container-spinner');
        this.foldersService.setInStandByState(folder.viewId, folder.id, standByDatas).subscribe(
            (folder) => {
                if (this.connectedUserStateService.getUserConnected().params.isFolderInStandByModeVisible) {
                    this.eventsManagerService.emitListFolderUpdatedEvent(folder.viewId, folder);
                    this.eventsManagerService.emitFollowedFoldersUpdatedEvent(folder.viewId, folder);
                } else {
                    // Ajout ou suppression des dossiers
                    this.eventsManagerService.emitListRefreshEvent();
                    this.eventsManagerService.emitFollowedFoldersRefreshEvent();

                    if (folder.boxId) {
                        this.eventsManagerService.emitBoxCounterUpdatedEvent(folder.boxId, -1);
                    } else {
                        this.eventsManagerService.emitViewCounterUpdatedEvent(folder.viewId, -1);
                    }
                }

                this.eventsManagerService.emitFolderMenuRefreshEvent(folder.id, MenuType.StandBy, true);

                this.spinner.hide('folders-container-spinner');
                this.notificationService.displaySuccess(`Le dossier '${folder.name}' a été mis en veille`, '');
            },
            (error: any) => {
                this.spinner.hide('folders-container-spinner');
            }
        );
    }

    /**
     * Retire le dossier du mode stand-by.
     * @param folder: Instance du dossier.
     */
    public removeFromStandByState(folder: Folder): void {
        this.spinner.show('folders-container-spinner');
        this.foldersService.removeFromStandByState(folder.viewId, folder.id).subscribe(
            (folder) => {
                if (this.connectedUserStateService.getUserConnected().params.isFolderInStandByModeVisible) {
                    // A faire uniquement si la vue du dossier correspond à la vue courante.
                    this.eventsManagerService.emitListFolderUpdatedEvent(folder.viewId, folder);
                    this.eventsManagerService.emitFollowedFoldersUpdatedEvent(folder.viewId, folder);
                } else {
                    // Ajout ou suppression des dossiers
                    this.eventsManagerService.emitListRefreshEvent();
                    this.eventsManagerService.emitFollowedFoldersRefreshEvent();

                    if (folder.boxId) {
                        this.eventsManagerService.emitBoxCounterUpdatedEvent(folder.boxId, +1);
                    } else {
                        this.eventsManagerService.emitViewCounterUpdatedEvent(folder.viewId, +1);
                    }
                }

                this.eventsManagerService.emitFolderMenuRefreshEvent(folder.id, MenuType.StandBy, false);

                this.spinner.hide('folders-container-spinner');
                this.notificationService.displaySuccess(`Le dossier '${folder.name}' a été sortie de veille`, '');
            },
            (error: any) => {
                this.spinner.hide('folders-container-spinner');
            }
        );
    }

    /**
     * Passe le dossier en attente d'archivage.
     * @param folder: Instance du dossier.
     */
    public SetInWaitingArchivingState(folder: Folder): void {
        this.spinner.show('folders-container-spinner');
        this.foldersService.SetInWaitingArchivingState(folder.viewId, folder.id).subscribe(
            (folder) => {
                if (this.connectedUserStateService.getUserConnected().params.isFolderInArchivingModeVisible) {
                    this.eventsManagerService.emitListFolderUpdatedEvent(folder.viewId, folder);
                    this.eventsManagerService.emitFollowedFoldersUpdatedEvent(folder.viewId, folder);
                } else {
                    // Ajout ou suppression des dossiers
                    this.eventsManagerService.emitListRefreshEvent();
                    this.eventsManagerService.emitFollowedFoldersRefreshEvent();

                    if (folder.boxId) {
                        this.eventsManagerService.emitBoxCounterUpdatedEvent(folder.boxId, -1);
                    } else {
                        this.eventsManagerService.emitViewCounterUpdatedEvent(folder.viewId, -1);
                    }
                }

                this.eventsManagerService.emitFolderTabCloseRequestEvent(folder.id, false);
                // this.eventsManagerService.emitFolderMenuRefreshEvent(folder.id, MenuType.WaitingArchiving, true);
                // this.eventsManagerService.emitHistorySectionUpdatedEvent(folder.viewId, folder.id);

                this.spinner.hide('folders-container-spinner');
                this.notificationService.displaySuccess(`Le dossier '${folder.name}' a été placé en attente d'archivage`, '');
            },
            (error: any) => {
                this.spinner.hide('folders-container-spinner');
            }
        );
    }

    /**
     * Retire le dossier de l'état en attente d'archivage
     * @param folder: Instance du dossier.
     */
    public removeFromWaitingArchivingState(folder: Folder): void {
        this.spinner.show('folders-container-spinner');
        this.foldersService.removeFromWaitingArchivingState(folder.viewId, folder.id).subscribe(
            (folder) => {
                if (this.connectedUserStateService.getUserConnected().params.isFolderInArchivingModeVisible) {
                    // A faire uniquement si la vue du dossier correspond à la vue courante.
                    this.eventsManagerService.emitListFolderUpdatedEvent(folder.viewId, folder);
                    this.eventsManagerService.emitFollowedFoldersUpdatedEvent(folder.viewId, folder);
                } else {
                    // Ajout ou suppression des dossiers
                    this.eventsManagerService.emitListRefreshEvent();
                    this.eventsManagerService.emitFollowedFoldersRefreshEvent();

                    if (folder.boxId) {
                        this.eventsManagerService.emitBoxCounterUpdatedEvent(folder.boxId, +1);
                    } else {
                        this.eventsManagerService.emitViewCounterUpdatedEvent(folder.viewId, +1);
                    }
                }

                this.eventsManagerService.emitFolderMenuRefreshEvent(folder.id, MenuType.WaitingArchiving, false);
                this.eventsManagerService.emitHistorySectionUpdatedEvent(folder.viewId, folder.id);

                this.spinner.hide('folders-container-spinner');
                this.notificationService.displaySuccess(`Le dossier '${folder.name}' a été retiré de l'attente d'archivage`, '');
            },
            (error: any) => {
                this.spinner.hide('folders-container-spinner');
            }
        );
    }

    /**
     * Archive le dossier.
     * @param folder: Instance du dossier.
     */
    public archive(folder: Folder): void {
        this.spinner.show('folders-container-spinner');
        this.spinner.show('detail-spinner');
        this.foldersService.archive(folder.viewId, folder.id).subscribe(
            (x) => {
                this.eventsManagerService.emitListRefreshEvent();
                this.eventsManagerService.emitFollowedFoldersRefreshEvent();

                this.spinner.hide('folders-container-spinner');
                this.spinner.hide('detail-spinner');
                this.notificationService.displaySuccess(`Le dossier '${folder.name}' a été archivé avec succès`, '');
                this.eventsManagerService.emitFolderTabCloseRequestEvent(folder.id, true);
            },
            (error: any) => {
                this.spinner.hide('folders-container-spinner');
                this.spinner.hide('detail-spinner');
            }
        );
    }

    public exportFoldersList(): void {
        this.eventsManagerService.emitFolderListExportEvent();
    }

    /**
     * Suit le dossier.
     * @param folder: Instance du dossier.
     */
    public followFolder(folder: Folder): void {
        this.spinner.show('folders-container-spinner');
        this.foldersService.followFolder(folder.viewId, folder.id).subscribe({
            next: () => {
                if (this.connectedUserStateService.getUserConnected().params.isFolderInStandByModeVisible) {
                    // A faire uniquement si la vue du dossier correspond à la vue courante.
                    this.eventsManagerService.emitListFolderUpdatedEvent(folder.viewId, folder);
                    this.eventsManagerService.emitFollowedFoldersUpdatedEvent(folder.viewId, folder);
                } else {
                    // Ajout ou suppression des dossiers
                    this.eventsManagerService.emitListRefreshEvent();
                    this.eventsManagerService.emitFollowedFoldersRefreshEvent();

                    if (folder.boxId) {
                        this.eventsManagerService.emitBoxCounterUpdatedEvent(folder.boxId, +1);
                    } else {
                        this.eventsManagerService.emitViewCounterUpdatedEvent(folder.viewId, +1);
                    }
                }

                this.eventsManagerService.emitFolderMenuRefreshEvent(folder.id, MenuType.FollowFolder, true);

                this.spinner.hide('folders-container-spinner');
                this.notificationService.displaySuccess(`Suivi du dossier '${folder.name}'`, '');
            },
            error: (error: any) => {
                this.spinner.hide('folders-container-spinner');
            },
        });
    }

    /**
     * Arrête le suivi du dossier.
     * @param folder: Instance du dossier.
     */
    public stopFollowFolder(folder: Folder): void {
        this.spinner.show('folders-container-spinner');
        this.foldersService.stopFollowFolder(folder.viewId, folder.id).subscribe({
            next: () => {
                if (this.connectedUserStateService.getUserConnected().params.isFolderInStandByModeVisible) {
                    // A faire uniquement si la vue du dossier correspond à la vue courante.
                    this.eventsManagerService.emitListFolderUpdatedEvent(folder.viewId, folder);
                    this.eventsManagerService.emitFollowedFoldersUpdatedEvent(folder.viewId, folder);
                } else {
                    // Ajout ou suppression des dossiers
                    this.eventsManagerService.emitListRefreshEvent();
                    this.eventsManagerService.emitFollowedFoldersRefreshEvent();

                    if (folder.boxId) {
                        this.eventsManagerService.emitBoxCounterUpdatedEvent(folder.boxId, +1);
                    } else {
                        this.eventsManagerService.emitViewCounterUpdatedEvent(folder.viewId, +1);
                    }
                }

                this.eventsManagerService.emitFolderMenuRefreshEvent(folder.id, MenuType.FollowFolder, false);

                this.spinner.hide('folders-container-spinner');
                this.notificationService.displaySuccess(`Arrêt de suivi du dossier '${folder.name}'`, '');
            },
            error: (error: any) => {
                this.spinner.hide('folders-container-spinner');
            },
        });
    }
}
